import { PickersDay, StaticDatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { Box, Paper } from '@mui/material'
import TextField from '@mui/material/TextField'
import PropTypes from 'prop-types'
import React, { useEffect, useState, useMemo } from 'react'
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz'
import { isSameDay, isValid } from 'date-fns'

import { RANGE_DAY, STATIC_WRAPPER_TYPE, VARIANTS, DATE_PICKER_TYPE } from './date-picker.constants'
import { handleStylesWithProps, styles } from './date-picker.styles'
import { calculateNewRange } from './date-picker.utils'

const Day = (props) => {
  const { selectedDay, variant, range, timezoneToday, shouldGreyOutDate, ...pickersDayProps } =
    props
  const { day, outsideCurrentMonth } = pickersDayProps
  const stylesWithProps = handleStylesWithProps({
    date: day,
    variant,
    range,
    selectedDay,
    isOutsideCurrentMonth: outsideCurrentMonth,
  })

  // Override the "today" indicator to use timezone-aware today
  const isToday = timezoneToday && isSameDay(day, timezoneToday)
  const isGreyedOut = shouldGreyOutDate && shouldGreyOutDate(day)

  return (
    <Box sx={stylesWithProps.pickersDayContainer}>
      <PickersDay
        disableMargin
        sx={[styles.pickersDay, stylesWithProps.pickersDay, isGreyedOut && styles.greyedOutDay]}
        {...pickersDayProps}
        today={isToday}
      />
    </Box>
  )
}

Day.propTypes = {
  day: PropTypes.object,
  selectedDay: PropTypes.object,
  outsideCurrentMonth: PropTypes.bool,
  variant: PropTypes.oneOf(Object.values(VARIANTS)),
  range: PropTypes.array,
  timezoneToday: PropTypes.object,
  shouldGreyOutDate: PropTypes.func,
}

const DatePicker = ({
  picker,
  variant,
  value,
  onChange,
  minDate,
  maxDate,
  disablePast,
  timezone,
  onViewChange: onViewChangeProp,
  shouldGreyOutDate,
  ...other
}) => {
  const isRange = variant === VARIANTS.RANGE
  const initialRange =
    isRange && Array.isArray(value) && value.length === 2 && isValid(value[0]) && isValid(value[1])
      ? value
      : [maxDate || new Date(), maxDate || new Date()]

  const [selectedDay, setSelectedDay] = useState(
    isRange ? initialRange[1] || new Date() : value || maxDate || new Date()
  )
  const [range, setRange] = useState(initialRange)
  const [rangeDay, setRangeDay] = useState(RANGE_DAY.FIRST)
  const [monthDate, setMonthDate] = useState(
    !isRange && value && isValid(value) ? value : new Date()
  )
  const [view, setView] = useState('day')

  // Calculate timezone-aware "today" for the day indicator
  const timezoneToday = useMemo(() => {
    if (!timezone) return new Date()
    const now = new Date()
    const todayStr = formatInTimeZone(now, timezone, 'yyyy-MM-dd')
    return zonedTimeToUtc(`${todayStr}T00:00:00`, timezone)
  }, [timezone])

  useEffect(() => {
    if (!isRange) return
    const newRange = maxDate ? calculateNewRange(selectedDay, range, rangeDay, setRangeDay) : value

    onChange(newRange)
    setRange(newRange)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sync range state when value prop changes externally (e.g., form reset, report switch)
  useEffect(() => {
    if (
      isRange &&
      Array.isArray(value) &&
      value.length === 2 &&
      isValid(value[0]) &&
      isValid(value[1])
    ) {
      const rangeChanged = !isSameDay(value[0], range[0]) || !isSameDay(value[1], range[1])
      if (rangeChanged) {
        setRange(value)
      }
    }
  }, [isRange, value]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isRange && value && value !== selectedDay) {
      setSelectedDay(value)
    }
  }, [isRange, value, selectedDay])

  const handleChange = (newValue) => {
    if (variant === VARIANTS.RANGE) {
      // For range picker, normalize to midnight in browser timezone (legacy behavior)
      const newDate = new Date(newValue)
      newDate.setHours(0, 0, 0, 0)
      const newRange = calculateNewRange(newDate, range, rangeDay, setRangeDay)
      setRange(newRange)
      onChange(newRange)
      return
    }
    // For single date picker, trust MUI's timezone handling - don't manipulate the date
    setSelectedDay(newValue)
    onChange(newValue)
  }

  const stylesWithProps = handleStylesWithProps({ monthDate })
  return (
    <Paper sx={{ ...styles.root, ...stylesWithProps.root }} id="date-picker-container">
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <StaticDatePicker
          test-id={`static-date-picker`}
          id="date-picker"
          displayStaticWrapperAs={STATIC_WRAPPER_TYPE}
          value={isRange ? null : selectedDay}
          onChange={handleChange}
          closeOnSelect={false}
          view={view}
          onViewChange={(newView) => {
            setView(newView)
            if (onViewChangeProp) {
              onViewChangeProp(newView)
            }
          }}
          {...other}
          showDaysOutsideCurrentMonth
          slots={{
            textField: (params) => (
              <TextField {...params} test-id={`static-date-picker-textfield`} />
            ),
            day: Day,
          }}
          slotProps={{
            day: {
              selectedDay,
              range,
              variant,
              timezoneToday,
              shouldGreyOutDate,
            },
          }}
          onMonthChange={(value) => setMonthDate(new Date(value))}
          {...(minDate && { minDate })}
          {...(maxDate && { maxDate })}
          {...(disablePast && { disablePast })}
          {...(timezone && { timezone })}
        />
      </LocalizationProvider>
    </Paper>
  )
}

DatePicker.propTypes = {
  picker: PropTypes.string,
  variant: PropTypes.oneOf(Object.values(VARIANTS)),
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.array]),
  onChange: PropTypes.func,
  onViewChange: PropTypes.func,
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  disablePast: PropTypes.bool,
  timezone: PropTypes.string,
  shouldGreyOutDate: PropTypes.func,
}

DatePicker.defaultProps = {
  picker: null,
  variant: VARIANTS.DAY,
  value: null,
  onChange: () => {},
}

export default DatePicker
